'use client'

import Modal from '@/components/Modal/Modal'
import { Copy, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'react-toastify'

interface ErrorDebugModalProps {
  isOpen: boolean
  onClose: () => void
  errorData?: {
    toolName: string
    timestamp: string
    errorMessage: string
    summary?: string
  }
}

export function ErrorDebugModal({ isOpen, onClose, errorData }: ErrorDebugModalProps) {
  const [isCopying, setIsCopying] = useState(false)

  if (!errorData) return null

  const handleCopyToClipboard = async () => {
    try {
      setIsCopying(true)

      // Format error details for copying
      const formattedError = [
        `Tool: ${errorData.toolName}`,
        `Timestamp: ${errorData.timestamp}`,
        `Summary: ${errorData.summary || 'Tool execution failed'}`,
        ``,
        `Technical Details:`,
        errorData.errorMessage,
      ].join('\n')

      await navigator.clipboard.writeText(formattedError)
      toast.success('Error details copied to clipboard')
    } catch (error) {
      console.error('Copy failed:', error)
      toast.error('Failed to copy error details')
    } finally {
      setIsCopying(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Tool Error Details</h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Tool Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tool Name</label>
              <div className="mt-1 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
                {errorData.toolName}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Timestamp</label>
              <div className="mt-1 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-900">
                {errorData.timestamp}
              </div>
            </div>
          </div>

          {/* Error Summary */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Summary</label>
            <div className="mt-1 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900">
              {errorData.summary || 'Tool execution failed'}
            </div>
          </div>

          {/* Technical Details */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">Technical Details</label>
              <button
                onClick={handleCopyToClipboard}
                disabled={isCopying}
                className="flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50"
              >
                <Copy size={12} />
                {isCopying ? 'Copying...' : 'Copy'}
              </button>
            </div>
            <div className="max-h-64 overflow-auto rounded-md bg-gray-50 p-3 font-mono text-xs text-gray-900">
              {errorData.errorMessage}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Technical error details that can be shared with support if needed
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}
